import LogoUrl from '../../../assets/images/logo-big.avif';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation, PrefetchPageLinks } from 'react-router';
import { ROUTES, AUTH_SCREENS } from '@/constants';
import { ChevronLeftIcon } from 'lucide-react';
import {
	useRegisterWithEmailMutation,
	useLazyCheckEmailQuery,
	useGetProfileQuery,
	useSignOutMutation,
	useUpdateProfileMutation,
} from '@/store/api';
import { useIsRegisteredUser } from '../../../hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { isAtLeastMinimumSignupAge, parseBirthDate, isGroupRequired } from '../../../utils';
import { useSignUp } from '../../../contexts/signup.context';
import { toast } from '@/components/ui/use-toast';
import { usePostAuthFlow, useIsMobile, useIsMobileSafari } from '@/hooks';
import {
	cn,
	consumeSignUpAfterTradeAttempt,
	checkIsProbabilityFlow,
	getUtmFromStorage,
	getErrorMessage,
	inviteTokenPayload,
} from '@/utils';
import { UnauthLayout } from '@/components/auth/unauth-layout';
import { LegalAgreement } from '@/components/ui/legal-agreement';
import mixpanel from '@/configs/mixpanel';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { updateSessionAction } from '@/store/slices/auth/actions/update-session.action';

import { Slide } from './signup.types';
import { emailRegex, getActiveSignupSlides } from './signup.constants';
import { SignupLoadingView } from './signup-loading.view';
import { useSignupParentConsent } from './signup-parent-consent.hook';

interface SignupLocationState {
	slide?: 'nhssaa';
	consentAction?: 'change-parent-email';
	parentEmail?: string;
}

interface SignupViewProps {
	emailFromUrl?: string;
	redirectUrl?: string;
	isModal?: boolean;
	onClose?: () => void;
}

export default function SignupView({
	emailFromUrl,
	redirectUrl,
	isModal = false,
	onClose,
}: SignupViewProps) {
	const dispatch = useDispatch();
	const [isSignUpLoading, setIsSignUpLoading] = useState(false);
	const isNextInProgress = useRef(false);
	const [signUpWithEmail] = useRegisterWithEmailMutation();
	const [signOut] = useSignOutMutation();
	const [updateProfile] = useUpdateProfileMutation();
	const navigate = useNavigate();
	const location = useLocation();
	const signupLocationState = location.state as SignupLocationState | null;

	const utm = getUtmFromStorage();

	const [searchParams] = useSearchParams();
	const redirect = redirectUrl ?? searchParams.get('redirect');
	const isProbabilityFlow = checkIsProbabilityFlow(redirect);

	const isMobile = useIsMobile();
	const isMobileSafari = useIsMobileSafari();
	const {
		email,
		setEmail,
		fullName,
		setFullName,
		birthDate,
		setBirthDate,
		country,
		setCountry,
		college,
		setCollege,
		categories,
		group,
		setGroup,
		parentEmail,
		setParentEmail,
		imageId,
		imgVariants,
		setError,
		setIsOtpFlow,
		isAvatarUploading,
		isParentConsentSubmitting,
		setIsParentConsentSubmitting,
		setPreview,
		resetSignUp,
	} = useSignUp();

	const [triggerCheckEmail] = useLazyCheckEmailQuery();
	const { collectReferralData, handlePostAuth } = usePostAuthFlow();

	const isRegisteredUser = useIsRegisteredUser();
	const { data: user } = useGetProfileQuery(isRegisteredUser ? undefined : skipToken);
	const isChangingParentEmail =
		isRegisteredUser && signupLocationState?.consentAction === 'change-parent-email';
	const currentParentEmail = signupLocationState?.parentEmail?.trim();
	const { validateParentEmail, submitAuthenticatedConsent, requestInitialConsent } =
		useSignupParentConsent({
			isRegisteredUser,
			isChangingParentEmail,
			studentEmail: user?.email || email,
			currentParentEmail,
			setIsSignUpLoading,
			setIsParentConsentSubmitting,
		});
	const needsProbabilityUsername =
		isProbabilityFlow && (!isRegisteredUser || Boolean(user && !user.username?.trim()));

	const activeSlides = useMemo(
		() =>
			getActiveSignupSlides({
				isProbabilityFlow,
				needsProbabilityUsername,
				isNhssaaFlow: group === 'nhssaa' || signupLocationState?.slide === 'nhssaa',
				// Any 14–16 minor needs parental consent by age, not just NHSSAA. The
				// backend already flags them pending_consent, so the parent-email step
				// must surface for them too.
				requiresParentalConsent: isGroupRequired(birthDate),
				isChangingParentEmail,
				accountStatus: isRegisteredUser ? user?.account_status : undefined,
			}),
		[
			isProbabilityFlow,
			needsProbabilityUsername,
			group,
			signupLocationState?.slide,
			birthDate,
			isChangingParentEmail,
			user?.account_status,
			isRegisteredUser,
		]
	);

	const [currentSlideIndex, setCurrentSlide] = useState(() => {
		return signupLocationState?.slide === 'nhssaa' || group === 'nhssaa'
			? Math.max(
					0,
					activeSlides.findIndex((slide) => slide.id === 'nhssaa')
				)
			: 0;
	});
	const currentSlide = Math.min(currentSlideIndex, activeSlides.length - 1);
	useEffect(() => {
		if (isChangingParentEmail && currentParentEmail) {
			setParentEmail(currentParentEmail);
		}
	}, [isChangingParentEmail, currentParentEmail, setParentEmail]);
	useEffect(() => {
		if (user) {
			if (user.email) setEmail(user.email);
			if (user.full_name) setFullName(user.full_name);
			if (user.birthday) {
				const date = new Date(user.birthday);
				setBirthDate({
					month: format(date, 'MMM'),
					day: format(date, 'd'),
					year: format(date, 'yyyy'),
				});
			}
			if (user.country) setCountry(user.country);
			if (user.college) setCollege(user.college);
			setGroup(user.group || '');
			if (user.img_variants?.s) setPreview(user.img_variants.s);
		}
	}, [user, setEmail, setFullName, setBirthDate, setCountry, setCollege, setGroup, setPreview]);

	useEffect(() => {
		if (emailFromUrl) {
			setEmail(emailFromUrl);
			setIsOtpFlow(true);
		}
	}, [emailFromUrl, setEmail, setIsOtpFlow]);

	const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

	const validateBirthDateRequirements = async () => {
		const { day, month, year } = birthDate;
		if (!day || !month || !year) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Please select your birthdate',
			});
			return false;
		}

		// Universal parental consent: the group step is optional for 14–16 minors — they can
		// continue without selecting one and still get the age-gated parent-email step. NHSSAA
		// students self-select or arrive via their link; no forced pick.

		if (!isAtLeastMinimumSignupAge(parseBirthDate(birthDate))) {
			const target = `${ROUTES.AUTH}?screen=${AUTH_SCREENS.UNDERAGE}${redirect ? `&redirect=${encodeURIComponent(redirect)}` : ''}`;
			if (isRegisteredUser) {
				await signOut(undefined);
				window.location.assign(target);
			} else {
				resetSignUp();
				navigate(target);
			}
			return false;
		}

		return true;
	};

	const validateUsername = () => {
		if (!fullName.trim()) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Username is required',
			});
			return false;
		}

		if (fullName.trim().length > 25) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Username must be no longer than 25 characters',
			});
			return false;
		}

		return true;
	};

	const validateEmailDetails = async () => {
		if (!email.trim()) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Email is required',
			});
			return false;
		}

		if (!emailRegex.test(email)) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Please enter a valid email address',
			});
			return false;
		}

		try {
			const { isTaken } = await triggerCheckEmail(email).unwrap();

			if (isTaken) {
				toast({
					variant: 'destructive',
					title: 'Error',
					description: 'Email is already in use. Try a different one',
				});
				return false;
			}
		} catch {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to check email. Please try again.',
			});
			return false;
		}

		return true;
	};

	const handleRegisterSubmit = async () => {
		if (isRegisteredUser) {
			if (!user) return;
			setIsSignUpLoading(true);
			try {
				await updateProfile({
					id: user.id,
					full_name: fullName.trim(),
					birthday: parseBirthDate(birthDate),

					college: college || null,
					...(country ? { country } : {}),
					...(imageId ? { img_parent_id: imageId } : {}),
					...(imgVariants && Object.keys(imgVariants).length ? { img_variants: imgVariants } : {}),
				}).unwrap();

				toast({
					variant: 'success',
					title: 'Profile updated',
					description: 'Your setup is complete!',
				});

				if (isProbabilityFlow || window.location.pathname.includes('probability-event')) {
					const tradeAttempt = consumeSignUpAfterTradeAttempt();
					let authMethod = 'email';
					if ((user as any).googleEmail || (user as any).google_id) authMethod = 'google';
					else if (
						(user as any).appleId ||
						(user as any).appleEmail ||
						user.email?.includes('appleid.com')
					)
						authMethod = 'apple';

					const authCompletedProps = {
						...tradeAttempt,
						userEmail: user.email,
						username: user.full_name,
						auth_method: authMethod,
						is_new_registration: true,
					};
					mixpanel.track('[AUTH COMPLETED]', authCompletedProps);
					window.dataLayer?.push({
						event: 'auth_completed',
						...authCompletedProps,
					});
				}

				onClose?.();
			} catch (err: unknown) {
				setError(getErrorMessage(err, 'Failed to complete setup. Please try again.'));
				console.error(err);
				setIsSignUpLoading(false);
			}
			return;
		}

		if (!validateUsername()) return;
		if (!(await validateEmailDetails())) return;
		if (!(await validateBirthDateRequirements())) return;

		const birthday = parseBirthDate(birthDate);
		setIsSignUpLoading(true);

		try {
			if (!isModal) {
				await sleep(2500);
			}

			const { referralCode, ipAddress, fingerprint, data } = await collectReferralData();

			const { accessToken, user } = await signUpWithEmail({
				email: email.trim(),
				full_name: fullName.trim(),
				birthday,
				category_ids: categories,
				utmSource: utm?.utm_source,
				...(country ? { country } : {}),
				...(imageId ? { img_parent_id: imageId } : {}),
				...(imgVariants && Object.keys(imgVariants).length ? { img_variants: imgVariants } : {}),
				...(referralCode ? { referralCode, fingerprint, ipAddress } : {}),
				...inviteTokenPayload(redirect),
				...(college ? { college } : {}),
				...(group ? { group } : {}),
			}).unwrap();

			if (isProbabilityFlow) {
				localStorage.setItem(`profileSetupRedirected:${user.id}`, 'true');
			}

			toast({
				variant: 'success',
				title: 'Registration successful',
				description: 'Welcome! Your account has been created.',
			});

			const tradeAttempt = consumeSignUpAfterTradeAttempt();
			if (tradeAttempt) {
				mixpanel.track('[SIGN UP AFTER TRADE]', {
					...tradeAttempt,
					userEmail: email.trim(),
					username: fullName.trim(),
				});
			}

			if (isProbabilityFlow || window.location.pathname.includes('probability-event')) {
				const authCompletedProps = {
					...tradeAttempt,
					userEmail: email.trim(),
					username: fullName.trim(),
					auth_method: 'email',
					is_new_registration: true,
				};
				mixpanel.track('[AUTH COMPLETED]', authCompletedProps);
				window.dataLayer?.push({
					event: 'auth_completed',
					...authCompletedProps,
				});
			}

			if (isModal) {
				(dispatch as any)(updateSessionAction({ accessToken } as any));
				onClose?.();
			} else {
				await handlePostAuth({
					accessToken,
					referralCode,
					data,
					fingerprint,
					ipAddress,
					redirectUrl,
				});
			}
		} catch (err: unknown) {
			setError(getErrorMessage(err, 'Registration failed. Please check your input and try again.'));
			console.error(err);
			setIsSignUpLoading(false);
		}
	};

	const goToAuth = () => {
		const target = redirectUrl
			? `${ROUTES.AUTH}?redirect=${encodeURIComponent(redirectUrl)}`
			: ROUTES.AUTH;
		navigate(target);
	};

	const handleNext = async () => {
		if (isNextInProgress.current) return;
		isNextInProgress.current = true;
		try {
			const currentSlideObj = activeSlides[currentSlide];

			if (currentSlideObj.id === 'nhssaa') {
				const trimmedParentEmail = validateParentEmail(parentEmail);
				if (!trimmedParentEmail) return;
				if (await submitAuthenticatedConsent(trimmedParentEmail)) return;

				if (!validateUsername()) return;
				if (!(await validateEmailDetails())) return;
				if (!(await validateBirthDateRequirements())) return;

				const birthday = parseBirthDate(birthDate);
				setIsSignUpLoading(true);
				setIsParentConsentSubmitting(true);

				try {
					const { referralCode, ipAddress, fingerprint } = await collectReferralData();

					const { accessToken } = await signUpWithEmail({
						email: email.trim(),
						full_name: fullName.trim(),
						birthday,
						category_ids: categories,
						utmSource: utm?.utm_source,
						...(country ? { country } : {}),
						...(imageId ? { img_parent_id: imageId } : {}),
						...(imgVariants && Object.keys(imgVariants).length
							? { img_variants: imgVariants }
							: {}),
						...(referralCode ? { referralCode, fingerprint, ipAddress } : {}),
						...inviteTokenPayload(redirect),
						...(college ? { college } : {}),
						group: group || 'nhssaa',
					}).unwrap();

					// Store the JWT so the next call is authenticated
					await (dispatch as any)(updateSessionAction({ accessToken } as any));

					await requestInitialConsent(trimmedParentEmail);

					navigate(ROUTES.PARENT_EMAIL_VERIFICATION, {
						state: { email: trimmedParentEmail },
					});
					setIsParentConsentSubmitting(false);
				} catch (err: unknown) {
					toast({
						variant: 'destructive',
						title: 'Error',
						description: getErrorMessage(err, 'Something went wrong. Please try again.'),
					});
					setIsSignUpLoading(false);
					setIsParentConsentSubmitting(false);
				}
				return;
			}

			if (currentSlide === activeSlides.length - 1) {
				await handleRegisterSubmit();
				return;
			}

			if (currentSlideObj.id === 'user') {
				if (!validateUsername()) return;
				if (!isRegisteredUser) {
					if (!(await validateEmailDetails())) return;
				}
				if (!(await validateBirthDateRequirements())) return;

				setCurrentSlide(currentSlide + 1);
				return;
			}

			setCurrentSlide(currentSlide + 1);
		} finally {
			isNextInProgress.current = false;
		}
	};

	const handleBack = () => (currentSlide === 0 ? navigate(-1) : setCurrentSlide(currentSlide - 1));

	// 14–16 minors reach a parental-consent slide appended to the end of the flow.
	// The account is only created at that slide (signUpWithEmail + requestInitialConsent).
	const lastSlide = activeSlides[activeSlides.length - 1];
	const hasConsentStep = lastSlide?.id === 'nhssaa';

	// For minors, any skip must land on the consent slide — never finalize registration.
	// Finalizing here creates the account pending_consent with no consent request, which
	// strands the user (they can't even resend the parent email).
	const handleSkip = () => {
		if (hasConsentStep) {
			setCurrentSlide(activeSlides.length - 1);
			return;
		}
		handleRegisterSubmit();
	};

	const onSkip = () => {
		if (isRegisteredUser) {
			onClose?.();
			return;
		}
		if (hasConsentStep) {
			setCurrentSlide(activeSlides.length - 1);
			return;
		}
		if (currentSlide === activeSlides.length - 1) {
			handleRegisterSubmit();
			return;
		}
		if (currentSlide === 1) {
			setCurrentSlide(currentSlide + 1);
			return;
		}
		goToAuth();
	};

	const slide = (activeSlides[currentSlide] ?? activeSlides[activeSlides.length - 1]) as Slide;

	const slideDots = (
		<div className="flex gap-2">
			{activeSlides.map((_, idx) => (
				<div
					key={idx}
					className={`h-0.5 w-[64px] rounded-full ${idx === currentSlide ? 'bg-[#ACC0F6]' : 'bg-[#2D3A6C]'}`}
				/>
			))}
		</div>
	);
	const showSlideDots = activeSlides.length > 1;

	const slideHeader = (
		<div className="flex-1 flex flex-col items-center justify-center text-center gap-1">
			<div className="text-[28px] font-bold text-[#F7F7F8] whitespace-pre-wrap">{slide.title}</div>
			<p
				className={`text-sm max-w-sm text-[#B5B6BA] ${activeSlides[0] ? 'font-normal' : 'font-normal'} whitespace-pre-wrap`}
			>
				{slide.description}
			</p>
		</div>
	);

	if (isSignUpLoading || isParentConsentSubmitting) {
		return (
			<SignupLoadingView
				isModal={isModal}
				isMobile={isMobile}
				isParentConsentSubmitting={isParentConsentSubmitting}
			/>
		);
	}

	if (isMobile) {
		return (
			<div
				className={cn(
					"signup-mobile-auth flex min-h-dvh flex-col items-center bg-[#101423] bg-cover bg-center bg-no-repeat p-5 font-['DM_Sans']",
					isMobileSafari ? 'signup-mobile-auth-safari justify-between pb-10' : 'justify-start pb-5'
				)}
			>
				<PrefetchPageLinks page={ROUTES.PARENT_EMAIL_VERIFICATION} />
				<PrefetchPageLinks page={ROUTES.AUTH} />
				<div className="flex flex-col items-center gap-3 mt-12 w-full">
					{showSlideDots && (
						<div className="w-full flex justify-between items-center gap-7">
							<div className="w-[56px] h-[12px]" />
							{slideDots}
							<div className="w-[56px] h-[12px]" />
						</div>
					)}
					<span className="mb-5">{slideHeader}</span>
					<div className="w-full">
						<slide.Component />
					</div>
				</div>

				{!isProbabilityFlow && (slide as any).id !== 'nhssaa' && (
					<button
						onClick={handleBack}
						className="flex items-center text-sm text-[#B5B6BA] font-normal absolute top-8 left-5"
					>
						<ChevronLeftIcon className="text-[#F7F7F8]" strokeWidth={1.25} />
						Back
					</button>
				)}

				{currentSlide !== 0 && (slide as any).id !== 'nhssaa' && (
					<button
						onClick={onSkip}
						className="text-sm text-[#81A1F8] font-normal absolute top-8 right-5"
					>
						Skip
					</button>
				)}

				<div
					className={cn(
						'flex flex-col items-center gap-4 w-full',
						isMobileSafari ? 'mb-7' : 'mt-4'
					)}
				>
					{currentSlide === 0 && (
						<LegalAgreement variant={isProbabilityFlow ? 'probability-event' : 'default'} />
					)}
					<button
						disabled={isAvatarUploading}
						onClick={handleNext}
						className="bg-blue-custom-2 text-[#F7F7F8] h-10 w-full rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{slide.buttonText
							? slide.buttonText
							: isProbabilityFlow && currentSlide === activeSlides.length - 1
								? 'Save & Continue'
								: 'Next'}
					</button>
				</div>
			</div>
		);
	} else {
		if (isModal) {
			return (
				<div className="relative w-full flex flex-col items-center gap-6 pt-16 pb-8 px-6 font-dmsans bg-[#101423] text-white">
					{currentSlide !== 0 && (slide as any).id !== 'nhssaa' && (
						<div className="absolute top-[32px] right-[24px]">
							<button
								disabled={isSignUpLoading}
								onClick={onSkip}
								className="text-sm text-[#81A1F8] font-normal hover:text-[#ACC0F6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Skip
							</button>
						</div>
					)}

					<img src={LogoUrl} className="h-[56px] mb-2 object-contain" />

					{showSlideDots && (
						<div className="w-full max-w-[360px] flex justify-between items-center gap-7">
							<div className="w-[56px] h-[12px]" />
							{slideDots}
							<div className="w-[56px] h-[12px]" />
						</div>
					)}
					{slideHeader}

					<div className="w-full max-w-[360px]">
						<slide.Component />
					</div>

					<div className="flex flex-col items-center gap-5 w-full max-w-[360px]">
						{currentSlide === 0 && (
							<LegalAgreement variant={isProbabilityFlow ? 'probability-event' : 'default'} />
						)}
						{currentSlide !== 0 && !isProbabilityFlow && (slide as any).id !== 'nhssaa' ? (
							<div className="mt-2 flex items-center gap-3 w-full justify-center">
								<button
									disabled={isSignUpLoading}
									onClick={handleSkip}
									className="text-sm text-[#81A1F8] h-10 w-[158px] font-normal hover:text-[#ACC0F6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									Skip
								</button>
								<button
									disabled={isSignUpLoading || isAvatarUploading}
									onClick={handleNext}
									className="bg-blue-custom-2 text-[#F7F7F8] h-10 w-[158px] rounded-full text-sm font-semibold hover:bg-[#1b58f5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
								>
									{isSignUpLoading && currentSlide === activeSlides.length - 1 ? (
										<>
											<span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
											Saving...
										</>
									) : slide.buttonText ? (
										slide.buttonText
									) : (
										'Next'
									)}
								</button>
							</div>
						) : (
							<button
								disabled={isSignUpLoading || isAvatarUploading}
								onClick={handleNext}
								className="mt-2 bg-blue-custom-2 text-[#F7F7F8] h-10 w-full rounded-full text-sm font-semibold hover:bg-[#1b58f5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
							>
								{isSignUpLoading && currentSlide === activeSlides.length - 1 ? (
									<>
										<span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
										Saving...
									</>
								) : slide.buttonText ? (
									slide.buttonText
								) : isProbabilityFlow && currentSlide === activeSlides.length - 1 ? (
									'Save & Continue'
								) : (
									'Next'
								)}
							</button>
						)}
					</div>
				</div>
			);
		}

		return (
			<UnauthLayout className="font-dmsans">
				<PrefetchPageLinks page={ROUTES.PARENT_EMAIL_VERIFICATION} />
				<PrefetchPageLinks page={ROUTES.AUTH} />
				<div className="flex flex-col items-center gap-8">
					{(slide as any).id !== 'nhssaa' && (
						<div className="absolute top-[48px] left-[24px]">
							<button
								onClick={handleBack}
								className="flex items-center text-sm text-[#B5B6BA] font-normal"
							>
								<ChevronLeftIcon className="text-[#F7F7F8]" strokeWidth={1.25} />
								Back
							</button>
						</div>
					)}
					<img src={LogoUrl} className="h-[70px] mb-8" />

					{currentSlide === 2 && <div className="h-[12px]" />}

					{showSlideDots && (
						<div className="w-[400px] flex justify-between items-center gap-7">
							<div className="w-[56px] h-[36px]" />
							{slideDots}
							<div className="w-[56px] h-[12px]" />
						</div>
					)}
					{slideHeader}

					<div className="w-full">
						<slide.Component />
					</div>

					<div
						className={cn(
							'flex flex-col items-center gap-5 w-full',
							(slide as any).id === 'nhssaa' && 'px-4 sm:px-8 md:px-12'
						)}
					>
						{currentSlide === 0 && (
							<LegalAgreement variant={isProbabilityFlow ? 'probability-event' : 'default'} />
						)}
						{currentSlide !== 0 && !isProbabilityFlow && (slide as any).id !== 'nhssaa' ? (
							<div className="mt-2 flex items-center gap-3">
								<button
									onClick={handleSkip}
									className="text-sm text-[#81A1F8]  h-10 w-[158px] font-normal"
								>
									Skip
								</button>
								<button
									disabled={isAvatarUploading}
									onClick={handleNext}
									className="bg-blue-custom-2 text-[#F7F7F8] h-10 w-[158px] rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{slide.buttonText ? slide.buttonText : 'Next'}
								</button>
							</div>
						) : (
							<button
								disabled={isAvatarUploading}
								onClick={handleNext}
								className="mt-2 bg-blue-custom-2 text-[#F7F7F8] h-10 w-full rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{slide.buttonText
									? slide.buttonText
									: isProbabilityFlow && currentSlide === activeSlides.length - 1
										? 'Save & Continue'
										: 'Next'}
							</button>
						)}
					</div>
				</div>
			</UnauthLayout>
		);
	}
}
